// Apollo
import { apolloClient } from '@/graphql';
import gql from 'graphql-tag';

// GraphQL
import UpdateFinal from '@/graphql/finals/update/updateFinal.gql';
import UpdateFinalV2 from '@/graphql/finals/update/updateFinalV2.gql';
import UpdateJoinFinalsLeagues from '@/graphql/finals/update/updateJoinFinalsLeagues.gql';
import UpdateJoinTeamFinals from '@/graphql/finals/update/updateTeamFinalsJoins.gql';
import InsertFinals from '@/graphql/finals/insert/insertFinals.gql';
import InsertFinalsGroup from '@/graphql/finals/insert/insertFinalsGroups.gql';
import InsertFinalsKnockout from '@/graphql/finals/insert/insertFinalsKnockout.gql';
import InsertTeamFinalsJoins from '@/graphql/finals/insert/insertTeamFinalsJoins.gql';
import InsertLeagueFinalsJoins from '@/graphql/finals/insert/InsertLeagueFinalsJoins.gql';
import InsertFinalsMatch from '@/graphql/finals/insert/insertFinalsMatch.gql';
import DeleteFinal from '@/graphql/finals/delete/deleteFinal.gql';
import DeleteFinalsMatches from '@/graphql/finals/delete/deleteFinalsMatches.gql';

export default {
	state: {
		finals: [] as HasuraFinal[],
		total: null as number | null,
	},

	// ================ Getters ================
	getters: {
		finals(state: any) {
			return state.finals;
		},
		total(state: any) {
			return state.total;
		},
	},

	// ================ Mutations ================
	mutations: {
		setFinals(state: any, payload: { finals: HasuraFinal[]; total: number }) {
			state.finals = payload.finals;
			state.total = payload.total;
		},
	},

	// ================= Actions =================
	actions: {
		async getFinals({ commit }: any) {
			const response = await apolloClient.query({
				query: gql`
					query Finals {
						finals {
							id
							color
							color_name
							created_at
							day_of_the_week
							display_name
							kick_off_date
							location
							number_of_teams
							player_type
							published
							status
							team_size
							updated_at
							locationObj: locationByLocation {
								id
								name
								city {
									id
									name
									country {
										id
										name
									}
								}
							}
							finals_groups(order_by: { name: asc }) {
								id
								name
								parent_finals
								qualification_positions
								default_game_length
								default_number_of_halves
								default_half_length
								default_time_slot
								round_robin_format
								default_pitches
								number_of_teams
								frequency
								is_completed
								teamsInGroup: placeholderTeamsByParentGroup {
									id
									name
									type
									seeding_league
									seeding_position
									seeding_group
									parent_league
									parent_group
									parent_finals
								}
								matches {
									away_score
									away_team
									created_at
									date
									game_length
									home_score
									home_team
									pitch
									name
									id
									kick_off_time
									man_of_the_match
									name
									finals_group
									finals_knockout
									placeholder_away_team
									placeholder_home_team
									postponed
									round
									number_of_halves
									half_length
									referee
									slot_time
									deleted_at
									deleted_team
									refereeObj: refereeByReferee {
										id
										first_name
										last_name
									}
									pitchObj: pitchByPitch {
										id
										name
										pitch_name
										location {
											id
											name
											city_id
										}
									}
									awayTeamObj: team {
										id
										name
										type
									}
									homeTeamObj: teamByHomeTeam {
										id
										name
										type
									}
									phHomeTeamObj: placeholderTeamByPlaceholderHomeTeam {
										id
										name
										type
										seeding_position
										seeding_league
										seeding_group
										seeding_match
									}
									phAwayTeamObj: placeholder_team {
										id
										name
										type
										seeding_position
										seeding_league
										seeding_group
										seeding_match
									}
								}
							}
							finals_knockouts {
								id
								number_of_teams
								parent_finals
								updated_at
								default_slot_duration
								default_game_length
								default_number_of_halves
								default_half_length
								default_pitches
								number_of_teams
								frequency
								created_at
								matches {
									away_score
									away_team
									created_at
									date
									game_length
									home_score
									home_team
									pitch
									name
									id
									kick_off_time
									man_of_the_match
									name
									finals_group
									finals_knockout
									placeholder_away_team
									placeholder_home_team
									number_of_halves
									half_length
									postponed
									referee
									slot_time
									pens_winner
									refereeObj: refereeByReferee {
										id
										first_name
										last_name
									}
									pitchObj: pitchByPitch {
										id
										name
										pitch_name
										location {
											id
											name
											city_id
										}
									}
									awayTeamObj: team {
										id
										name
										type
									}
									homeTeamObj: teamByHomeTeam {
										id
										name
										type
									}
									phHomeTeamObj: placeholderTeamByPlaceholderHomeTeam {
										id
										name
										type
										seeding_position
										seeding_league
										seeding_group
										seeding_match
									}
									phAwayTeamObj: placeholder_team {
										id
										name
										type
										seeding_position
										seeding_league
										seeding_group
										seeding_match
									}
								}
							}
						}
					}
				`,
				fetchPolicy: 'no-cache',
			});

			const { finals } = response.data;
			commit('setFinals', finals);
		},
		async getFinalByActive({ commit, state }: any, id: string) {
			const response = await apolloClient.query({
				query: gql`
					query ActiveFinals {
						finals(where: { status: { _in: ["planned", "active"] } }) {
							id
							color
							color_name
							day_of_the_week
							display_name
							kick_off_date
							location
							number_of_teams
							player_type
							status
							team_size
							group_stage
							updated_at
							published
							play_off
							join_team_finals {
								team: teamByTeam {
									id
									name
									join_players_teams {
										player: playerByPlayer {
											id
											first_name
											last_name
										}
									}
								}
							}
							parent_leagues: join_finals_leagues(order_by: { leagueByLeague: { display_name: asc } }) {
								id
								league
								qualification_positions
								finals
								leagueObj: leagueByLeague {
									id
									display_name
									status
									finals: join_finals_leagues {
										qualification_positions
										finalObj: final {
											id
											display_name
										}
									}
									seeding_teams: placeholder_teams {
										id
										name
										type
										seeding_league
										seeding_position
										seeding_group
										parent_league
										parent_group
										parent_finals
									}
									league_teams(order_by: { placeholder_team: { name: asc } }) {
										id
										points_carried
										played_carried
										won_carried
										drawn_carried
										lost_carried
										gs_carried
										gd_carried
										deleted_at
										team {
											id
											name
										}
										placeholder_team {
											id
											name
										}
									}
								}
							}
							locationObj: locationByLocation {
								id
								name
								currency
								city {
									id
									name
								}
							}
							finals_groups(order_by: { name: asc }) {
								id
								name
								parent_finals
								qualification_positions
								default_game_length
								default_number_of_halves
								default_half_length
								default_time_slot
								round_robin_format
								default_pitches
								number_of_teams
								frequency
								is_completed
								teamsInGroup: placeholderTeamsByParentGroup {
									id
									name
									type
									seeding_league
									seeding_position
									seeding_group
									parent_league
									parent_group
									parent_finals
								}
								matches {
									away_score
									away_team
									created_at
									date
									game_length
									home_score
									home_team
									pitch
									name
									id
									kick_off_time
									man_of_the_match
									name
									finals_group
									finals_knockout
									placeholder_away_team
									placeholder_home_team
									postponed
									round
									number_of_halves
									half_length
									referee
									slot_time
									deleted_at
									deleted_team
									refereeObj: refereeByReferee {
										id
										first_name
										last_name
									}
									pitchObj: pitchByPitch {
										id
										name
										pitch_name
										location {
											id
											name
											city_id
										}
									}
									awayTeamObj: team {
										id
										name
										type
									}
									homeTeamObj: teamByHomeTeam {
										id
										name
										type
									}
									phHomeTeamObj: placeholderTeamByPlaceholderHomeTeam {
										id
										name
										type
										seeding_position
										seeding_league
										seeding_group
										seeding_match
									}
									phAwayTeamObj: placeholder_team {
										id
										name
										type
										seeding_position
										seeding_league
										seeding_group
										seeding_match
									}
								}
							}
							finals_knockouts {
								id
								number_of_teams
								parent_finals
								updated_at
								default_slot_duration
								default_game_length
								default_number_of_halves
								default_half_length
								default_pitches
								number_of_teams
								frequency
								created_at
								matches {
									away_score
									away_team
									created_at
									date
									game_length
									home_score
									home_team
									pitch
									name
									id
									kick_off_time
									man_of_the_match
									name
									finals_group
									finals_knockout
									placeholder_away_team
									placeholder_home_team
									number_of_halves
									half_length
									postponed
									referee
									slot_time
									pens_winner
									refereeObj: refereeByReferee {
										id
										first_name
										last_name
									}
									pitchObj: pitchByPitch {
										id
										name
										pitch_name
										location {
											id
											name
											city_id
										}
									}
									awayTeamObj: team {
										id
										name
										type
									}
									homeTeamObj: teamByHomeTeam {
										id
										name
										type
									}
									phHomeTeamObj: placeholderTeamByPlaceholderHomeTeam {
										id
										name
										type
										seeding_position
										seeding_league
										seeding_group
										seeding_match
									}
									phAwayTeamObj: placeholder_team {
										id
										name
										type
										seeding_position
										seeding_league
										seeding_group
										seeding_match
									}
								}
							}
							placeholder_teams {
								id
								name
								seeding_position
								seeding_league
								seeding_group
								seeding_match
							}
						}
					}
				`,
				fetchPolicy: 'no-cache',
				variables: {
					id,
				},
			});

			const { finals } = response.data;
			// commit('setSelectedLeague', finals[0]);
			commit('setFinals', finals);
			return finals[0];
		},
		async getFinalByStatus({ commit }: any, status: any) {
			const filters = status.filters;

			const pagination = status.pagination;
			const offset = (pagination.page - 1) * pagination.itemsPerPage;
			const limit = pagination.itemsPerPage;

			const now = new Date();
			const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
			const isoDate = oneDayAgo.toISOString();

			const statusConditions: any[] = [];

			if (filters.isActive) {
				statusConditions.push({
					_and: [{ published: { _eq: true } }, { kick_off_date: { _lt: isoDate } }, { status: { _neq: 'finished' } }],
				});
			}

			if (filters.isPlanned) {
				statusConditions.push(
					{
						_and: [{ kick_off_date: { _gte: isoDate } }, { status: { _neq: 'finished' } }],
					},
					{
						_and: [{ published: { _eq: false } }, { status: { _neq: 'finished' } }],
					}
				);
			}

			if (filters.isFinished) {
				statusConditions.push({
					_and: [{ status: { _eq: 'finished' } }, { published: { _eq: true } }],
				});
			}

			if (filters.isArchived) {
				statusConditions.push({
					_and: [{ status: { _eq: 'finished' } }, { published: { _eq: false } }],
				});
			}

			// sort by
			let orderBy: any = { kick_off_date: 'desc' };
			if (filters.sortBy === 'Date ASC') {
				orderBy = { kick_off_date: 'asc' };
			} else if (filters.sortBy === 'Date DESC') {
				orderBy = { kick_off_date: 'desc' };
			} else if (filters.sortBy === 'Created') {
				orderBy = { created_at: 'desc' };
			} else if (filters.sortBy == 'A-Z') {
				orderBy = {
					display_name: 'asc',
				};
			} else if (filters.sortBy == 'Z-A') {
				orderBy = {
					display_name: 'desc',
				};
			} else if (filters.sortBy == 'Results Needed') {
				orderBy = { kick_off_date: 'asc' };
			}

			const where: any = statusConditions.length > 0 ? { _or: statusConditions } : {};

			if (filters.searchField && filters.searchField.trim() !== '') {
				const searchFilter = {
					display_name: { _ilike: `%${filters.searchField.trim()}%` },
				};

				// Combine with existing filter using _and
				if (Object.keys(where).length > 0) {
					where._and = where._and || [];
					where._and.push(searchFilter);
				} else {
					Object.assign(where, searchFilter);
				}
			}

			//filter by country id

			if (filters.selectedCountry && filters.selectedCountry.trim() !== '') {
				const countryFilter = {
					locationByLocation: {
						city: {
							country: {
								id: { _eq: filters.selectedCountry },
							},
						},
					},
				};

				if (Object.keys(where).length > 0) {
					where._and = where._and || [];
					where._and.push(countryFilter);
				} else {
					Object.assign(where, countryFilter);
				}
			}

			// city filter
			if (filters.selectedCity && filters.selectedCity.trim() !== '') {
				const cityFilter = {
					locationByLocation: {
						city: {
							id: { _eq: filters.selectedCity },
						},
					},
				};

				if (Object.keys(where).length > 0) {
					where._and = where._and || [];
					where._and.push(cityFilter);
				} else {
					Object.assign(where, cityFilter);
				}
			}

			//filter by locations//venue
			if (filters.selectedLocation) {
				const locationFilter = {
					location: { _eq: filters.selectedLocation },
				};

				if (Object.keys(where).length > 0) {
					where._and = where._and || [];
					where._and.push(locationFilter);
				} else {
					Object.assign(where, locationFilter);
				}
			}

			if (filters.sortBy == 'Results Needed') {
				const isoDate = new Date(Date.now()).toISOString().split('T')[0];

				const strickCondition = {
					_and: [
						{ published: { _eq: true } },
						{ status: { _neq: 'finished' } },
						{
							_or: [
								// Check for unplayed matches in finals_groups
								{
									finals_groups: {
										matches: {
											_and: [{ home_score: { _is_null: true } }, { away_score: { _is_null: true } }, { date: { _lt: isoDate } }],
										},
									},
								},
								// Check for unplayed matches in finals_knockouts
								{
									finals_knockouts: {
										matches: {
											_and: [{ home_score: { _is_null: true } }, { away_score: { _is_null: true } }, { date: { _lt: isoDate } }],
										},
									},
								},
							],
						},
					],
				};

				if (Object.keys(where).length > 0) {
					where._and = where._and || [];
					where._and.push(strickCondition);
				} else {
					Object.assign(where, strickCondition);
				}
			}

			const response = await apolloClient.query({
				query: gql`
					query ArchivedFinals($where: finals_bool_exp, $limit: Int, $offset: Int, $orderBy: [finals_order_by!]) {
						finals(where: $where, order_by: $orderBy, limit: $limit, offset: $offset) {
							id
							color
							color_name
							day_of_the_week
							display_name
							kick_off_date
							location
							number_of_teams
							player_type
							status
							team_size
							group_stage
							updated_at
							published
							play_off
							join_team_finals {
								team: teamByTeam {
									id
									name
									join_players_teams {
										player: playerByPlayer {
											id
											first_name
											last_name
										}
									}
								}
							}
							parent_leagues: join_finals_leagues(order_by: { leagueByLeague: { display_name: asc } }) {
								id
								league
								qualification_positions
								finals
								leagueObj: leagueByLeague {
									id
									display_name
									status
									finals: join_finals_leagues {
										qualification_positions
										finalObj: final {
											id
											display_name
										}
									}
									seeding_teams: placeholder_teams {
										id
										name
										type
										seeding_league
										seeding_position
										seeding_group
										parent_league
										parent_group
										parent_finals
									}
									league_teams(order_by: { placeholder_team: { name: asc } }) {
										id
										points_carried
										played_carried
										won_carried
										drawn_carried
										lost_carried
										gs_carried
										gd_carried
										deleted_at
										team {
											id
											name
										}
										placeholder_team {
											id
											name
										}
									}
								}
							}
							locationObj: locationByLocation {
								id
								name
								currency
								city {
									id
									name
									country {
										id
										name
									}
								}
							}
							finals_groups(order_by: { name: asc }) {
								id
								name
								parent_finals
								qualification_positions
								default_game_length
								default_number_of_halves
								default_half_length
								default_time_slot
								round_robin_format
								default_pitches
								number_of_teams
								frequency
								is_completed
								teamsInGroup: placeholderTeamsByParentGroup {
									id
									name
									type
									seeding_league
									seeding_position
									seeding_group
									parent_league
									parent_group
									parent_finals
								}
								matches {
									away_score
									away_team
									created_at
									date
									game_length
									home_score
									home_team
									pitch
									name
									id
									kick_off_time
									man_of_the_match
									name
									finals_group
									finals_knockout
									placeholder_away_team
									placeholder_home_team
									postponed
									round
									number_of_halves
									half_length
									referee
									slot_time
									deleted_at
									deleted_team
									refereeObj: refereeByReferee {
										id
										first_name
										last_name
									}
									pitchObj: pitchByPitch {
										id
										name
										pitch_name
										location {
											id
											name
											city_id
										}
									}
									awayTeamObj: team {
										id
										name
										type
									}
									homeTeamObj: teamByHomeTeam {
										id
										name
										type
									}
									phHomeTeamObj: placeholderTeamByPlaceholderHomeTeam {
										id
										name
										type
										seeding_position
										seeding_league
										seeding_group
										seeding_match
									}
									phAwayTeamObj: placeholder_team {
										id
										name
										type
										seeding_position
										seeding_league
										seeding_group
										seeding_match
									}
								}
							}
							finals_knockouts {
								id
								number_of_teams
								parent_finals
								updated_at
								default_slot_duration
								default_game_length
								default_number_of_halves
								default_half_length
								default_pitches
								number_of_teams
								frequency
								created_at
								matches {
									away_score
									away_team
									created_at
									date
									game_length
									home_score
									home_team
									pitch
									name
									id
									kick_off_time
									man_of_the_match
									name
									finals_group
									finals_knockout
									placeholder_away_team
									placeholder_home_team
									number_of_halves
									half_length
									postponed
									referee
									slot_time
									pens_winner
									refereeObj: refereeByReferee {
										id
										first_name
										last_name
									}
									pitchObj: pitchByPitch {
										id
										name
										pitch_name
										location {
											id
											name
											city_id
										}
									}
									awayTeamObj: team {
										id
										name
										type
									}
									homeTeamObj: teamByHomeTeam {
										id
										name
										type
									}
									phHomeTeamObj: placeholderTeamByPlaceholderHomeTeam {
										id
										name
										type
										seeding_position
										seeding_league
										seeding_group
										seeding_match
									}
									phAwayTeamObj: placeholder_team {
										id
										name
										type
										seeding_position
										seeding_league
										seeding_group
										seeding_match
									}
								}
							}
							placeholder_teams {
								id
								name
								seeding_position
								seeding_league
								seeding_group
								seeding_match
							}
						}
						finals_aggregate(where: $where) {
							aggregate {
								count
							}
						}
					}
				`,
				fetchPolicy: 'no-cache',
				variables: {
					where,
					limit: limit,
					offset: offset,
					orderBy: orderBy,
				},
			});

			const { finals } = response.data;

			const total = response.data.finals_aggregate.aggregate.count || 0;

			// You can commit a mutation or do other operations with the response here
			commit('setFinals', { finals, total }); // Example mutation
			return { finals, total };
		},
		async getFinalById({ commit, state }: any, id: string) {
			const response = await apolloClient.query({
				query: gql`
					query getFinal($id: uuid!) {
						finals(where: { id: { _eq: $id } }) {
							id
							color
							color_name
							day_of_the_week
							display_name
							kick_off_date
							location
							number_of_teams
							player_type
							status
							team_size
							group_stage
							updated_at
							published
							play_off
							join_team_finals {
								team: teamByTeam {
									id
									name
									join_players_teams {
										player: playerByPlayer {
											id
											first_name
											last_name
										}
									}
								}
							}
							parent_leagues: join_finals_leagues(order_by: { leagueByLeague: { display_name: asc } }) {
								id
								league
								qualification_positions
								finals
								leagueObj: leagueByLeague {
									id
									display_name
									status
									finals: join_finals_leagues {
										qualification_positions
										finalObj: final {
											id
											display_name
										}
									}
									seeding_teams: placeholder_teams {
										id
										name
										type
										seeding_league
										seeding_position
										seeding_group
										parent_league
										parent_group
										parent_finals
									}
									league_teams(order_by: { placeholder_team: { name: asc } }) {
										id
										points_carried
										played_carried
										won_carried
										drawn_carried
										lost_carried
										gs_carried
										gd_carried
										deleted_at
										team {
											id
											name
										}
										placeholder_team {
											id
											name
										}
									}
								}
							}
							locationObj: locationByLocation {
								id
								name
								currency
								city {
									id
									name
								}
							}
							finals_groups(order_by: { name: asc }) {
								id
								name
								parent_finals
								qualification_positions
								default_game_length
								default_number_of_halves
								default_half_length
								default_time_slot
								round_robin_format
								default_pitches
								number_of_teams
								frequency
								is_completed
								teamsInGroup: placeholderTeamsByParentGroup {
									id
									name
									type
									seeding_league
									seeding_position
									seeding_group
									parent_league
									parent_group
									parent_finals
								}
								matches {
									away_score
									away_team
									created_at
									date
									game_length
									home_score
									home_team
									pitch
									name
									id
									kick_off_time
									man_of_the_match
									name
									finals_group
									finals_knockout
									placeholder_away_team
									placeholder_home_team
									postponed
									round
									number_of_halves
									half_length
									referee
									slot_time
									deleted_at
									deleted_team
									refereeObj: refereeByReferee {
										id
										first_name
										last_name
									}
									pitchObj: pitchByPitch {
										id
										name
										pitch_name
										location {
											id
											name
											city_id
										}
									}
									awayTeamObj: team {
										id
										name
										type
									}
									homeTeamObj: teamByHomeTeam {
										id
										name
										type
									}
									phHomeTeamObj: placeholderTeamByPlaceholderHomeTeam {
										id
										name
										type
										seeding_position
										seeding_league
										seeding_group
										seeding_match
									}
									phAwayTeamObj: placeholder_team {
										id
										name
										type
										seeding_position
										seeding_league
										seeding_group
										seeding_match
									}
								}
							}
							finals_knockouts {
								id
								number_of_teams
								parent_finals
								updated_at
								default_slot_duration
								default_game_length
								default_number_of_halves
								default_half_length
								default_pitches
								number_of_teams
								frequency
								created_at
								matches {
									away_score
									away_team
									created_at
									date
									game_length
									home_score
									home_team
									pitch
									name
									id
									kick_off_time
									man_of_the_match
									name
									finals_group
									finals_knockout
									placeholder_away_team
									placeholder_home_team
									number_of_halves
									half_length
									postponed
									referee
									slot_time
									pens_winner
									refereeObj: refereeByReferee {
										id
										first_name
										last_name
									}
									pitchObj: pitchByPitch {
										id
										name
										pitch_name
										location {
											id
											name
											city_id
										}
									}
									awayTeamObj: team {
										id
										name
										type
									}
									homeTeamObj: teamByHomeTeam {
										id
										name
										type
									}
									phHomeTeamObj: placeholderTeamByPlaceholderHomeTeam {
										id
										name
										type
										seeding_position
										seeding_league
										seeding_group
										seeding_match
									}
									phAwayTeamObj: placeholder_team {
										id
										name
										type
										seeding_position
										seeding_league
										seeding_group
										seeding_match
									}
								}
							}
							placeholder_teams {
								id
								name
								seeding_position
								seeding_league
								seeding_group
								seeding_match
							}
						}
					}
				`,
				fetchPolicy: 'no-cache',
				variables: {
					id,
				},
			});

			const { finals } = response.data;
			// commit('setSelectedLeague', finals[0]);

			return finals[0];
		},
		async getParentLeagues({ commit, state }: any, id: string) {
			const response = await apolloClient.query({
				query: gql`
					query getFinal($id: uuid!) {
						finals(where: { id: { _eq: $id } }) {
							id
							parent_leagues: join_finals_leagues(order_by: { leagueByLeague: { display_name: asc } }) {
								id
								league
								qualification_positions
								finals
								leagueObj: leagueByLeague {
									id
									display_name
									status
									finals: join_finals_leagues {
										qualification_positions
										finalObj: final {
											id
											display_name
										}
									}
									seeding_teams: placeholder_teams {
										id
										name
										type
										seeding_league
										seeding_position
										seeding_group
										parent_league
										parent_group
										parent_finals
									}
									league_teams(order_by: { placeholder_team: { name: asc } }) {
										team {
											id
											name
										}
										placeholder_team {
											id
											name
										}
									}
									matches {
										home_team
										away_team
										placeholder_home_team
										placeholder_away_team
										away_score
										home_score
										homeTeamObj: teamByHomeTeam {
											id
											name
										}
										awayTeamObj: team {
											id
											name
										}
										phAwayTeamObj: placeholder_team {
											id
											name
										}
										phHomeTeamObj: placeholderTeamByPlaceholderHomeTeam {
											id
											name
										}
									}
								}
							}
						}
					}
				`,
				fetchPolicy: 'no-cache',
				variables: {
					id,
				},
			});

			const { finals } = response.data;
			// commit('setSelectedLeague', finals[0]);

			return finals[0];
		},

		async insertFinals({ dispatch }: any, payload: any) {
			try {
				const objects: HasuraFinalSeed[] = [...payload];
				const res: any = await apolloClient.mutate({
					mutation: InsertFinals,
					// Parameters
					variables: {
						objects,
					},
				});

				dispatch('getFinals');
				return res.data.insert_finals.returning;
			} catch (error) {
				console.log('error');
				return false;
			}
		},

		async insertFinalsGroups({ dispatch }: any, payload: any) {
			const objects: HasuraFinalGroupSeed[] = [...payload];

			const res: any = await apolloClient
				.mutate({
					mutation: InsertFinalsGroup,
					// Parameters
					variables: {
						objects,
					},
				})
				.catch(err => {
					console.debug(err);
				});

			dispatch('getFinals');
			return res.data.insert_finals_groups.returning;
		},

		async insertFinalsKnockout({ dispatch }: any, payload: any) {
			const objects: HasuraFinalsKnockout[] = [...payload];

			const res: any = await apolloClient
				.mutate({
					mutation: InsertFinalsKnockout,
					// Parameters
					variables: {
						objects,
					},
				})
				.catch(err => {
					console.debug(err);
				});

			dispatch('getFinals');
			return res.data.insert_finals_knockouts.returning;
		},

		async insertLeagueFinalsJoins({ dispatch }: any, payload: any) {
			const objects: HasuraFinalGroupSeed[] = [...payload];

			const res: any = await apolloClient
				.mutate({
					mutation: InsertLeagueFinalsJoins,
					// Parameters
					variables: {
						objects,
					},
				})
				.catch(err => {
					console.debug(err);
				});

			dispatch('getFinals');
			return res.data.insert_join_finals_leagues.returning;
		},

		async insertTeamFinalsJoins({ dispatch }: any, joins: any) {
			joins = joins.filter((join: any) => join.team !== undefined);
			const promises = joins.map((j: { team: string; finals: string }) => {
				return apolloClient.mutate({
					mutation: InsertTeamFinalsJoins,
					// Parameters
					variables: {
						object: j,
					},
				});
			});

			const res = Promise.all(promises).catch(err => {
				console.log(err);
				console.log(joins);
				throw new Error('insertTeamFinalsJoins Failed');
			});

			return res;
		},

		async insertFinalsMatches({ dispatch }: any, { features, payload }: any) {
			const objects: HasuraFinalsMatchSeed[] = [...payload].map((m: any) => {
				return {
					...m,
					home_team: m.home_team instanceof Object ? m.home_team.id : m.home_team,
					away_team: m.away_team instanceof Object ? m.away_team.id : m.away_team,
					placeholder_home_team: m.placeholder_home_team instanceof Object ? m.placeholder_home_team.id : m.placeholder_home_team,
					placeholder_away_team: m.placeholder_away_team instanceof Object ? m.placeholder_away_team.id : m.placeholder_away_team,
					location: features.location,
				};
			});

			objects.map((m: any) => {
				if (!m.home_team && !m.placeholder_home_team) {
					console.log(payload);
					console.log(objects);
					throw new Error('Team or teams missing from match');
				}
			});

			const res: any = await apolloClient
				.mutate({
					mutation: InsertFinalsMatch,
					// Parameters
					variables: {
						objects,
					},
				})
				.catch(err => {
					console.debug(err);
					console.log('ERROR: insertFinalsMatches');
					console.log(payload);
				});

			// dispatch('getFinals');
			return res.data.insert_matches.returning;
		},

		async deleteFinal({ dispatch }: any, payload: any) {
			const { id } = payload;
			await apolloClient
				.mutate({
					mutation: DeleteFinal,
					// Parameters
					variables: {
						id,
					},
				})
				.catch(err => {
					console.debug(err);
				});

			dispatch('getFinals');
			return;
		},

		async deleteFinalsMatches({ dispatch }: any, ids: any) {
			const promises = ids.map((id: string) => {
				return apolloClient.mutate({
					mutation: DeleteFinalsMatches,
					// Parameters
					variables: {
						id,
					},
				});
			});

			await Promise.all(promises).catch(err => {
				console.debug(err);
			});

			return;
		},

		async updateFinal({ dispatch }: any, payload: any) {
			const { id, name, countryId } = payload;
			await apolloClient.mutate({
				mutation: UpdateFinal,
				// Parameters
				variables: {
					cityId: id,
					name,
					countryId,
				},
			});

			dispatch('getFinals');
			return;
		},

		async updateFinalsV2({ dispatch }: any, payload: any) {
			console.log('updating finals...');
			await apolloClient.mutate({
				mutation: UpdateFinalV2,
				// Parameters
				variables: {
					id: payload.id,
					object: payload,
				},
			});

			return;
		},

		async updateJoinFinalsLeagues({ dispatch }: any, payload: any[]) {
			console.log('updateJoinFinalsLeagues');
			const promises = payload.map((jfl: any) => {
				return apolloClient.mutate({
					mutation: UpdateJoinFinalsLeagues,
					// Parameters
					variables: {
						id: jfl.id,
						object: jfl,
					},
				});
			});

			await Promise.all(promises);

			return;
		},

		async updateFinalsGroups({ dispatch }: any, updatedGroups: any) {
			console.log('updatedGroups');
			console.log(updatedGroups);
			const promises = updatedGroups.map((g: any) => {
				return apolloClient.mutate({
					mutation: gql`
						mutation updateFinalsGroups($id: uuid!, $object: finals_groups_set_input!) {
							update_finals_groups(where: { id: { _eq: $id } }, _set: $object) {
								affected_rows
							}
						}
					`,
					variables: {
						id: g.id,
						object: g,
					},
				});
			});

			const res = Promise.all(promises).catch(err => {
				console.log(err);
				console.log(err.message);
			});

			if (res) {
				return res;
			} else {
				throw new Error('Error: updateFinalsGroups');
			}
		},

		async updateJoinTeamFinals({ dispatch }: any, joins: any) {
			joins = joins.filter((join: any) => join !== undefined);
			console.log('updating team finals joins...');
			const promises = joins.map(({ finals, oldTeam, newTeam }: { oldTeam: string; finals: string; newTeam: string }) => {
				return apolloClient.mutate({
					mutation: UpdateJoinTeamFinals,
					// Parameters
					variables: {
						finals,
						oldTeam,
						newTeam,
					},
				});
			});

			const res = Promise.all(promises).catch(err => {
				console.log(err);
				console.log(joins);
				throw new Error('insertTeamFinalsJoins Failed');
			});

			return res;
		},

		async getFinalsByTeam({ dispatch }: any, id: any) {
			console.log('getting finals for team...');

			const res = await apolloClient.mutate({
				mutation: UpdateJoinTeamFinals,
				// Parameters
				variables: {
					id,
				},
			});

			console.log(res);

			return res;
		},
	},
};
